import { rangeAddress } from "./a1";
import { compressFormulaGrid } from "./formulaPatterns";
import { summariseFormats } from "./formatRuns";
import { ExtractionPlan, SheetBounds, planExtraction } from "./extractionPlan";
import {
  CellValue,
  DefinedNameModel,
  SheetModel,
  SheetRow,
  TableModel,
  WorkbookModel,
} from "./workbookModel";

// Reading the live workbook.
//
// Thin on purpose. Every decision worth testing -- how much to read, how to compress
// formulas, how to summarise formats -- lives in a pure module beside this one; what
// is left here is Excel API plumbing that cannot be unit tested, so there should be as
// little of it as possible. If logic starts accumulating here, it belongs next door.
//
// The batching is the part to be careful with. Each context.sync() is a round trip, so
// the reads are staged rather than interleaved:
//
//   sync 1  what is here: sheet list, used-range bounds, tables, defined names
//   ---     decide per sheet how much to read (extractionPlan), with no data loaded
//   sync 2  the planned ranges only: values, formulas, number formats
//
// Reading bounds before data is the whole point. Pulling a 50,000-row used range is
// several seconds and a large allocation; loading it and trimming afterwards would pay
// exactly the cost the plan exists to avoid.

/** Cap on rows fed back per sheet, as a backstop against a pathological plan. */
const MAX_ROWS_PER_SHEET = 2_000;

/**
 * Excel's getSpecialCells can return only the formula-bearing cells rather than the
 * whole rectangle, which would make scanning a vast sheet cheap. Not used yet: its
 * requirement set is documented inconsistently (1.2 in one place, 1.9 in another, and
 * RangeAreas itself is 1.9) against our manifest floor of ExcelApi 1.5, and it needs a
 * real multi-million-cell workbook to validate against. See docs/assumptions.md A-17.
 *
 * Passed as `false` so the plan falls back to "scan the whole used range if it is
 * small enough, and say so plainly when it is not".
 */
const CAN_SCAN_FORMULAS_SPARSELY = false;

interface PendingSheet {
  name: string;
  position: number;
  hidden: boolean;
  isActive: boolean;
  bounds: SheetBounds | null;
  plan: ExtractionPlan;
  tables: TableModel[];
  /** One loaded range per planned row range, in the same order. */
  loaded: {
    values: Excel.Range;
    startRow: number;
  }[];
  /** The whole used range's formulas, when the plan called for a full scan. */
  scan: Excel.Range | null;
}

export async function extractWorkbook(): Promise<WorkbookModel> {
  return Excel.run(async (context) => {
    const worksheets = context.workbook.worksheets;
    worksheets.load("items/name,items/position,items/visibility");

    const active = worksheets.getActiveWorksheet();
    active.load("name");

    const names = context.workbook.names;
    names.load("items/name,items/type,items/value,items/scope");

    await context.sync();

    // ---- bounds and structure, still no data ----
    const usedRanges = worksheets.items.map((sheet) => {
      const used = sheet.getUsedRangeOrNullObject(true);
      // isNullObject is not loaded: for a *OrNullObject method it is readable after the
      // next sync without one, and loading it is a needless navigational read.
      used.load("address,rowCount,columnCount,rowIndex,columnIndex");

      const tables = sheet.tables;
      tables.load("items/name,items/showHeaders");
      return { sheet, used, tables };
    });

    await context.sync();

    // Table header rows need their own pass: the collection had to load first.
    const tableHeaders = usedRanges.map(({ tables }) =>
      tables.items.map((table) => {
        const range = table.getRange();
        range.load("address,rowCount");
        const header = table.showHeaders ? table.getHeaderRowRange() : null;
        header?.load("values");
        return { table, range, header };
      })
    );

    const pending: PendingSheet[] = usedRanges.map(({ sheet, used }) => {
      const bounds: SheetBounds | null = used.isNullObject
        ? null
        : {
            sheetName: sheet.name,
            firstRow: used.rowIndex,
            firstColumn: used.columnIndex,
            rowCount: used.rowCount,
            columnCount: used.columnCount,
            canScanFormulasSparsely: CAN_SCAN_FORMULAS_SPARSELY,
          };

      const plan = bounds
        ? planExtraction(bounds)
        : { values: "all" as const, rowRanges: [], scanFormulas: false, elision: null };

      const loaded: PendingSheet["loaded"] = [];
      if (bounds) {
        for (const rowRange of plan.rowRanges) {
          if (rowRange.count <= 0) continue;
          const count = Math.min(rowRange.count, MAX_ROWS_PER_SHEET);
          const range = sheet.getRangeByIndexes(
            bounds.firstRow + rowRange.start,
            bounds.firstColumn,
            count,
            bounds.columnCount
          );
          range.load("values,formulas,numberFormat");
          loaded.push({ values: range, startRow: rowRange.start });
        }
      }

      // A full-sheet formula scan reads formulas ONLY -- never values -- because the
      // values are what make a big read expensive and the plan already decided we are
      // not sending them.
      let scan: Excel.Range | null = null;
      if (bounds && plan.scanFormulas && plan.values === "sampled") {
        scan = sheet.getRangeByIndexes(
          bounds.firstRow,
          bounds.firstColumn,
          bounds.rowCount,
          bounds.columnCount
        );
        scan.load("formulas");
      }

      return {
        name: sheet.name,
        position: sheet.position,
        hidden: sheet.visibility !== Excel.SheetVisibility.visible,
        isActive: sheet.name === active.name,
        bounds,
        plan,
        tables: [],
        loaded,
        scan,
      };
    });

    await context.sync();

    // ---- everything is loaded; build the model ----
    const sheets: SheetModel[] = pending.map((entry, index) => {
      entry.tables = tableHeaders[index].map(({ table, range, header }) => ({
        name: table.name,
        range: stripSheetName(range.address),
        headers: (header?.values?.[0] ?? []).map(String),
        // The header row is not data, so it does not count towards the table's rows.
        rowCount: Math.max(0, range.rowCount - (table.showHeaders ? 1 : 0)),
      }));
      return buildSheet(entry);
    });

    const definedNames: DefinedNameModel[] = names.items.map((item) => ({
      name: item.name,
      refersTo: String(item.value ?? ""),
      scope: item.scope === Excel.NamedItemScope.worksheet ? "worksheet" : "workbook",
    }));

    const incompleteSheets = sheets
      .filter((sheet) => !sheet.dataComplete || !sheet.logicComplete)
      .map((sheet) => sheet.name);

    return {
      sheets,
      definedNames,
      ...(incompleteSheets.length > 0 ? { incompleteSheets } : {}),
    };
  });
}

function buildSheet(entry: PendingSheet): SheetModel {
  const { bounds, plan } = entry;

  const base: SheetModel = {
    name: entry.name,
    position: entry.position,
    isActive: entry.isActive,
    hidden: entry.hidden,
    usedRange: null,
    rowCount: 0,
    columnCount: 0,
    formulaPatterns: [],
    formulaExceptions: [],
    logicComplete: true,
    numberFormats: [],
    tables: entry.tables,
    rows: [],
    dataComplete: true,
  };

  if (!bounds) return base;

  const rows: SheetRow[] = [];
  const formulaGrids: { grid: string[][]; startRow: number }[] = [];
  const formatGrids: { grid: string[][]; startRow: number }[] = [];

  for (const { values, startRow } of entry.loaded) {
    const valueGrid = (values.values ?? []) as CellValue[][];
    valueGrid.forEach((cells, offset) => {
      rows.push({
        // 1-based, so it matches the row number the user sees in Excel.
        row: bounds.firstRow + startRow + offset + 1,
        cells: cells.map(normaliseCell),
      });
    });

    formulaGrids.push({ grid: toStringGrid(values.formulas), startRow });
    formatGrids.push({ grid: toStringGrid(values.numberFormat), startRow });
  }

  // Formulas: the whole-sheet scan when there was one, otherwise the sampled rows.
  const formulaSource = entry.scan
    ? [{ grid: toStringGrid(entry.scan.formulas), startRow: 0 }]
    : formulaGrids;

  const patterns = formulaSource.map(({ grid, startRow }) =>
    compressFormulaGrid(grid, bounds.firstRow + startRow, bounds.firstColumn)
  );

  const formats = formatGrids.flatMap(({ grid, startRow }) =>
    summariseFormats(grid, bounds.firstRow + startRow, bounds.firstColumn)
  );

  const headerRow = rows[0];

  return {
    ...base,
    usedRange: rangeAddress(
      bounds.firstRow,
      bounds.firstColumn,
      bounds.rowCount,
      bounds.columnCount
    ),
    rowCount: bounds.rowCount,
    columnCount: bounds.columnCount,
    ...(headerRow && looksLikeHeader(headerRow)
      ? { headers: headerRow.cells.map((cell) => String(cell ?? "")) }
      : {}),
    formulaPatterns: patterns.flatMap((p) => p.patterns),
    formulaExceptions: patterns.flatMap((p) => p.exceptions),
    logicComplete: plan.scanFormulas,
    numberFormats: formats,
    tables: entry.tables,
    rows,
    dataComplete: plan.values === "all",
    ...(plan.elision ? { elision: plan.elision } : {}),
  };
}

/**
 * A first row of non-empty strings with no numbers among them is a header row. Not
 * infallible, but the cost of being wrong is only that the model reads the first data
 * row as labels -- which it can see for itself, since the row is also in `rows`.
 */
function looksLikeHeader(row: SheetRow): boolean {
  const populated = row.cells.filter((cell) => cell !== null && cell !== "");
  if (populated.length === 0) return false;
  return populated.every((cell) => typeof cell === "string");
}

/** Excel returns addresses as "Sheet1!A1:D9"; the sheet is already on the model. */
function stripSheetName(address: string): string {
  const bang = address.lastIndexOf("!");
  return bang === -1 ? address : address.slice(bang + 1);
}

/** The API's cell values are `any`; narrow them to what the model promises. */
function normaliseCell(value: unknown): CellValue {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number" || typeof value === "boolean" || typeof value === "string") {
    return value;
  }
  return String(value);
}

function toStringGrid(grid: unknown): string[][] {
  if (!Array.isArray(grid)) return [];
  return grid.map((row) =>
    Array.isArray(row)
      ? row.map((cell) => (cell === null || cell === undefined ? "" : String(cell)))
      : []
  );
}
